import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';

import Logo from 'assets/images/logo.svg';
import { registration } from 'api/auth/registration';
import { schemaStep1Register } from 'api/auth/types';

const Registration = () => {
  const navigate = useNavigate();

  const [step] = useState(1);
  const [registeredEmail] = useState('');

  const [isVisible, setIsVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const formStep1 = useForm({
    resolver: zodResolver(schemaStep1Register),
    defaultValues: { email: '', password: '', confirmPassword: '' },
  });

  const registerMutation = useMutation({
    mutationFn: registration,
    onSuccess: response => {
      if (response?.status === 201) {
        enqueueSnackbar('Registration successful. Please check your email link.', {
          variant: 'success',
        });

        navigate('/login');
      }
    },
    onError: (error: any) => {
      const errorData = error.response?.data;

      if (errorData) {
        const errorKeys = Object.keys(errorData);

        const firstKey = errorKeys[0];

        const errorMessage = errorData[firstKey][0];

        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Something went wrong', { variant: 'error' });
      }
    },
  });

  const onSubmitStep1 = (data: any) => {
    setErrorMessage(null);
    registerMutation.mutate({
      email: data.email,
      password: data.password,
    });
  };

  const handleChangeIsVisible = useCallback(() => {
    setIsVisible(!isVisible);
  }, [isVisible]);

  return (
    <div className="auth-page-wrapper pt-5">
      <div className="auth-page-content">
        <div className="container">
          {/* Header / Logo */}
          <div className="row">
            <div className="col-lg-12">
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div>
                  <a href="https://trojan.systems/" className="d-inline-block auth-logo">
                    <img src={Logo} alt="" height="50" />
                  </a>
                </div>
                <p className="mt-3 fs-15 fw-medium text-primary">Smart pest management</p>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6 col-xl-5">
              <div className="card mt-4">
                <div className="card-body p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">{step === 1 ? 'Create account' : 'Verify Email'}</h5>
                    {step === 2 && (
                      <p className="text-muted">
                        Code sent to <b>{registeredEmail}</b>
                      </p>
                    )}
                  </div>

                  <div className="p-2 mt-4">
                    {/* --- STEP 1 FORM --- */}
                    {step === 1 && (
                      <form onSubmit={formStep1.handleSubmit(onSubmitStep1)}>
                        <div className="mb-3">
                          <label className="form-label mb-0">
                            Email <span className="text-danger">*</span>
                          </label>
                          <Controller
                            control={formStep1.control}
                            name="email"
                            render={({ field }) => (
                              <input
                                {...field}
                                type="email"
                                className="form-control"
                                placeholder="Enter email"
                                required
                              />
                            )}
                          />
                          {formStep1.formState.errors.email && (
                            <span className="text-danger text-sm">{formStep1.formState.errors.email.message}</span>
                          )}
                        </div>

                        <div className="mb-3">
                          <label className="form-label mb-0">
                            Password <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <Controller
                              control={formStep1.control}
                              name="password"
                              render={({ field }) => (
                                <input
                                  {...field}
                                  type={isVisible ? 'text' : 'password'}
                                  className="form-control"
                                  placeholder="Enter password"
                                  required
                                />
                              )}
                            />
                            <button className="btn btn-light" type="button" onClick={handleChangeIsVisible}>
                              <i className="ri-eye-fill align-middle" />
                            </button>
                          </div>
                          {formStep1.formState.errors.password && (
                            <span className="text-danger text-sm">{formStep1.formState.errors.password.message}</span>
                          )}
                        </div>

                        <div className="mb-3">
                          <label className="form-label mb-0">
                            Confirm Password <span className="text-danger">*</span>
                          </label>
                          <Controller
                            control={formStep1.control}
                            name="confirmPassword"
                            render={({ field }) => (
                              <input
                                {...field}
                                type={isVisible ? 'text' : 'password'}
                                className="form-control"
                                placeholder="Confirm password"
                                required
                              />
                            )}
                          />
                          {formStep1.formState.errors.confirmPassword && (
                            <span className="text-danger d-block mt-1">
                              {formStep1.formState.errors.confirmPassword.message}
                            </span>
                          )}
                        </div>

                        <div className="mt-4">
                          <button
                            type="submit"
                            className="btn text-light w-100"
                            style={{ backgroundColor: '#6836D3' }}
                            disabled={registerMutation.isLoading}
                          >
                            {registerMutation.isLoading ? 'Registering...' : 'Register'}
                          </button>
                        </div>
                      </form>
                    )}

                    {/* --- STEP 2 FORM --- */}
                    {/* {step === 2 && (
                      <form onSubmit={formStep2.handleSubmit(onSubmitStep2)}>
                        <div className="mb-3">
                          <label className="form-label">
                            Verification Code <span className="text-danger">*</span>
                          </label>
                          <Controller
                            control={formStep2.control}
                            name="code"
                            render={({ field }) => (
                              <input
                                {...field}
                                type="text"
                                className="form-control"
                                placeholder="Enter 6-digit code"
                                maxLength={6}
                                autoFocus
                                required
                              />
                            )}
                          />
                          {formStep2.formState.errors.code && (
                            <span className="text-danger">{formStep2.formState.errors.code.message}</span>
                          )}
                        </div>

                        <div className="mt-4">
                          <button
                            type="submit"
                            className="btn text-light w-100"
                            style={{ backgroundColor: '#6836D3' }}
                            disabled={verifyMutation.isLoading}
                          >
                            {verifyMutation.isLoading ? 'Verifying...' : 'Verify Code'}
                          </button>
                        </div>

                        <div className="mt-4 text-center">
                          <button
                            type="button"
                            className="btn btn-link text-muted text-decoration-none"
                            onClick={() => setStep(1)}
                          >
                            <i className="ri-arrow-left-line me-1" /> Back to Registration
                          </button>
                        </div>
                      </form>
                    )} */}

                    {/* Global Error Message */}
                    {errorMessage && (
                      <div className="alert alert-danger mt-3 text-center" role="alert">
                        {errorMessage}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <p className="mb-0">
                  Already have an account?{' '}
                  <Link to="/login" className="fw-semibold text-primary text-decoration-underline">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="text-center">
                <p className="mb-0 text-muted">© {new Date().getFullYear()} Trojan LLC.</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Registration;
